/** Strike-mission modules: the auto-scrolling route (`world.scrollTrack`,
 * the composed camera that carries the ground past a view-space craft and
 * places a seeded, fair strike route as it comes into view), the
 * telegraphing emplacement (`ai.turret`), the primary-target checklist with
 * its extraction (`rule.targets`) and visible damage stages
 * (`fx.condition`). */
import type { JsonObject } from '../../json.js';
import { id, int, list, num, obj, oneOf, readsSignal } from '../schemaBuilders.js';
import { defineModule, num as read, type ModuleCtx } from '../module.js';
import { addTag, removeTag, type EntityV1 } from '../world.js';
import { centreOf } from './flight.js';

const rad = (deg: number) => (deg * Math.PI) / 180;

export type RouteVelocity = { vx: number; vy: number };
export type RouteWave = {
  archetype: string;
  /** Sector-relative window (fractions of its length) the wave runs in. */
  from: number; to: number;
  /** Along-route spacing between consecutive members (seeded in [min, max]). */
  everyMin: number; everyMax: number;
  /** Across-route offset range (0 = the craft's line). */
  acrossMin: number; acrossMax: number;
  /** Own velocity given at spawn (a truck driving its road), view units/s. */
  velocity?: RouteVelocity;
};
export type RouteEscort = { archetype: string; along: number; across: number };
export type RouteMark = {
  archetype: string;
  /** Position as a fraction of the sector, ± a seeded along jitter. */
  at: number; jitter: number;
  across: number;
  label?: string;
  tint?: number;
  escorts: RouteEscort[];
};
export type RouteSector = { length: number; waves: RouteWave[]; marks: RouteMark[] };
export type ScrollTrackConfig = {
  /** Travel direction: degrees from straight up, clockwise (0 = a vertical
   * scroller; 25 = a shallow pseudo-isometric diagonal toward the upper right;
   * 180 = a descent whose course rises from the bottom of the view). */
  heading: number;
  /** Ground speed, world units per second (times `speedScalar` when set). */
  speed: number;
  /** A named scalar a body publishes that multiplies `speed` — the view
   * travels at the pace the player sets (a skier's downhill speed); 0 until
   * it is first published. Absent: the view travels at `speed` alone. */
  speedScalar?: string;
  /** Ground-fixed entities: they move with the ground. */
  anchorTag: string;
  /** The view point the route is measured at (where the craft flies). */
  anchor: { x: number; y: number };
  /** Entities appear this far outside the view (and ground leaves past it). */
  margin: number;
  /** The opening corridor: nothing is placed before this along distance. */
  safeAlong: number;
  /** Guaranteed along spacing between any two marks (jitter included): one
   * blast can never reach two of them. */
  markGap: number;
  /** Waves never place a member within this along distance of a mark or
   * an escort (the line through a gate stays open). Absent: no clearance. */
  markClear?: number;
  sectors: RouteSector[];
  /** The route's end marker (an extraction pad), placed at its last unit. */
  finale: { archetype: string; across: number };
};

const velocitySchema = obj({ vx: num(-20, 20), vy: num(-20, 20) });
const waveSchema = obj({ archetype: id(), from: num(0, 1), to: num(0, 1), everyMin: num(0.5, 400), everyMax: num(0.5, 400),
  acrossMin: num(-32, 32), acrossMax: num(-32, 32), velocity: velocitySchema }, ['velocity']);
const markSchema = obj({ archetype: id(), at: num(0, 1), jitter: num(0, 16), across: num(-32, 32),
  label: { type: 'string', maxLength: 24 }, tint: int(0, 15), escorts: list(obj({ archetype: id(), along: num(-16, 16), across: num(-16, 16) }), 4) },
['label', 'tint']);

type TrackCtx = ModuleCtx<ScrollTrackConfig>;

/** Unit forward (flight) and across (right of flight) vectors in view space. */
function axes(heading: number): { f: { x: number; y: number }; n: { x: number; y: number } } {
  const t = rad(heading);
  // Exact zeros on the axis headings (sin 180° is 1e-16, not 0): a straight
  // descent's ground never drifts sideways.
  const exact = (v: number) => (Math.abs(v) < 1e-12 ? 0 : v);
  const sin = exact(Math.sin(t)), cos = exact(Math.cos(t));
  return { f: { x: sin, y: -cos }, n: { x: cos, y: sin } };
}

function routeLength(c: ScrollTrackConfig): number {
  return c.sectors.reduce((sum, s) => sum + s.length, 0);
}

/** Nominal mark positions (no jitter), in route order. */
function markSpans(c: ScrollTrackConfig): Array<{ lo: number; hi: number }> {
  let base = 0;
  const out: Array<{ lo: number; hi: number }> = [];
  for (const s of c.sectors) {
    for (const m of s.marks) out.push({ lo: base + m.at * s.length - m.jitter, hi: base + m.at * s.length + m.jitter });
    base += s.length;
  }
  return out.sort((a, b) => a.lo - b.lo);
}

/** The seeded route: every placement with its along/across coordinates. */
function generateRoute(ctx: TrackCtx): JsonObject[] {
  const c = ctx.config, route: JsonObject[] = [], clear = c.markClear ?? 0;
  const place = (a: number, across: number, archetype: string, extra: JsonObject = {}) =>
    route.push({ a, c: across, archetype, spawned: false, ...extra });
  let base = 0;
  c.sectors.forEach((s, index) => {
    const sector = index + 1;
    const marked: number[] = [];
    for (const m of s.marks) {
      const a = base + m.at * s.length + (ctx.random() * 2 - 1) * m.jitter;
      place(a, m.across, m.archetype, { sector, ...(m.label !== undefined ? { label: m.label } : {}), ...(m.tint !== undefined ? { tint: m.tint } : {}) });
      marked.push(a);
      for (const escort of m.escorts) { place(a + escort.along, m.across + escort.across, escort.archetype, { sector }); marked.push(a + escort.along); }
    }
    for (const w of s.waves) {
      const end = base + w.to * s.length;
      let a = Math.max(base + w.from * s.length, c.safeAlong);
      for (a += w.everyMin + ctx.random() * (w.everyMax - w.everyMin); a < end; a += w.everyMin + ctx.random() * (w.everyMax - w.everyMin)) {
        // The across draw is taken either way, so a clearance never shifts the rest of the route.
        const across = w.acrossMin + ctx.random() * (w.acrossMax - w.acrossMin);
        if (marked.some(at => Math.abs(at - a) < clear)) continue;
        place(a, across, w.archetype, { sector, ...(w.velocity ? { vx: w.velocity.vx, vy: w.velocity.vy } : {}) });
      }
    }
    base += s.length;
  });
  place(base, c.finale.across, c.finale.archetype, { sector: c.sectors.length });
  // Stable: equal along positions keep generation order.
  return route.map((item, i) => ({ item, i })).sort((x, y) => (x.item.a as number) - (y.item.a as number) || x.i - y.i).map(x => x.item);
}

/** Where a route point is in the view (its centre) at travelled distance d. */
function project(c: ScrollTrackConfig, a: number, across: number, d: number): { x: number; y: number } {
  const { f, n } = axes(c.heading);
  return { x: c.anchor.x + (a - d) * f.x + across * n.x, y: c.anchor.y + (a - d) * f.y + across * n.y };
}

/** The ground speed this tick: `speed`, times the pace scalar when set. */
function groundSpeed(ctx: TrackCtx): number {
  const c = ctx.config;
  return c.speed * (c.speedScalar === undefined ? 1 : ctx.scalar(c.speedScalar, 0));
}

function publish(ctx: TrackCtx): void {
  const c = ctx.config, s = ctx.state, d = read(s, 'd'), total = routeLength(c), speed = groundSpeed(ctx);
  let sector = 1, base = 0;
  for (const sec of c.sectors) { if (d < base + sec.length) break; base += sec.length; sector = Math.min(c.sectors.length, sector + 1); }
  const { f } = axes(c.heading);
  ctx.setScalar('route.distance', d);
  ctx.setScalar('route.remaining', Math.max(0, total - d));
  ctx.setScalar('route.progress', Math.min(1, d / total));
  ctx.setScalar('sector.number', sector);
  // The ground's velocity in the view (it moves against the flight).
  ctx.setScalar('ground.vx', -f.x * speed);
  ctx.setScalar('ground.vy', -f.y * speed);
}

/** `world.scrollTrack` — the auto-scrolling camera as a world module. The
 * view travels along a heading (any direction: a strike run's diagonal, a
 * descent's straight-down) at a ground speed, optionally paced by a body's
 * scalar (a diagonal is a heading, not a projection: sprites stay
 * axis-aligned and the ground art scrolls by the published `view` offset). Ground-fixed entities move with the ground;
 * the seeded route places each sector's marks (with escorts), waves and the
 * finale as they come into view, only beyond the opening corridor and with
 * marks a guaranteed gap apart. Publishes `route.*`, `sector.number` and the
 * ground velocity (`ground.vx/vy`, the lead a dropped store needs); signals
 * `route-end` once the finale reaches the craft's line. */
export const scrollTrack = defineModule<ScrollTrackConfig>({
  id: 'world.scrollTrack', version: 1, attach: 'global', role: 'world',
  summary: 'Auto-scrolling camera over a seeded route (a strike run, a ski course): ground-fixed entities scroll past at a fixed or body-set pace, sectors place marks with escorts and waves as they come into view (safe opening corridor, spaced marks, waves clear of marks), the route end is signalled.',
  config: obj({ heading: num(-180, 180), speed: num(0.2, 20), speedScalar: id(), anchorTag: id(), anchor: obj({ x: num(0, 64), y: num(0, 64) }),
    margin: num(0, 1), safeAlong: num(0, 2000), markGap: num(0, 400), markClear: num(0, 40),
    sectors: list(obj({ length: num(4, 4000), waves: list(waveSchema, 6), marks: list(markSchema, 8) }), 6, 1),
    finale: obj({ archetype: id(), across: num(-32, 32) }) }, ['speedScalar', 'markClear']),
  requires: [], events: ['sector', 'route-end'],
  presentation: { sector: { cue: 'whoosh' }, 'route-end': { cue: 'none' } },
  proofs: ['ground-scrolls-along-heading', 'places-in-view', 'seeded-route', 'opening-corridor', 'marks-spaced', 'culls-passed-ground', 'route-end-once', 'view-offset',
    'paced-by-scalar', 'waves-clear-of-marks'],
  animStates: () => [],
  mapArchetypeRefs: (c, rename) => ({ ...c, finale: { ...c.finale, archetype: rename(c.finale.archetype) },
    sectors: c.sectors.map(s => ({ ...s, waves: s.waves.map(w => ({ ...w, archetype: rename(w.archetype) })),
      marks: s.marks.map(m => ({ ...m, archetype: rename(m.archetype), escorts: m.escorts.map(e => ({ ...e, archetype: rename(e.archetype) })) })) })) }),
  check(c, context) {
    const errors: string[] = [];
    if (c.anchor.x > context.world.w || c.anchor.y > context.world.h) errors.push('the anchor must lie in the view');
    c.sectors.forEach((s, i) => {
      for (const w of s.waves) {
        if (w.from >= w.to) errors.push(`sector ${i + 1}: a wave's window must run forward`);
        if (w.everyMin > w.everyMax || w.acrossMin > w.acrossMax) errors.push(`sector ${i + 1}: a wave's ranges must be ordered`);
      }
      for (const m of s.marks) if (m.tint !== undefined && m.tint >= context.paletteSize) errors.push(`sector ${i + 1}: mark tint outside the palette`);
    });
    let base = 0;
    for (const s of c.sectors) {
      for (const m of s.marks) {
        const earliest = base + m.at * s.length - m.jitter + Math.min(0, ...m.escorts.map(e => e.along));
        if (earliest < c.safeAlong) errors.push(`${m.archetype}: every mark and escort must lie beyond the opening corridor`);
      }
      base += s.length;
    }
    const spans = markSpans(c);
    for (let i = 1; i < spans.length; i++) if (spans[i]!.lo - spans[i - 1]!.hi < c.markGap) errors.push(`marks ${i} and ${i + 1} can fall closer than markGap`);
    return errors;
  },
  hud: () => [{ id: 'distance', label: 'DIST', kind: 'counter' }, { id: 'sector', label: 'SECTOR', kind: 'counter' }],
  hudValues: ctx => ({ distance: Math.ceil(ctx.scalar('route.remaining', 0)), sector: ctx.scalar('sector.number', 1) }),
  // The seeded route is the whole schedule: what it has not placed yet
  // (an item that passed out of view unplaced counts as placed).
  placementsAhead: (ctx, kinds) => (ctx.state.route as JsonObject[]).filter(item => item.spawned !== true && kinds.has(item.archetype as string)).length,
  initGlobal(ctx) {
    Object.assign(ctx.state, { d: 0, next: 0, ended: false, sector: 1, route: generateRoute(ctx) });
    publish(ctx);
  },
  systems: {
    // Globals move first: the ground (and what stands on it) is where it is
    // this tick before any body steers or collides.
    move(ctx) {
      const c = ctx.config, s = ctx.state, { f } = axes(c.heading);
      const step = groundSpeed(ctx) * ctx.dt;
      s.d = read(s, 'd') + step;
      const gx = -f.x * step, gy = -f.y * step, { w, h } = ctx.world.bounds, m = c.margin;
      for (const e of ctx.entities({ tag: c.anchorTag, status: 'any' })) {
        e.x += gx; e.y += gy;
        // Ground leaves past the view edges it flows toward (any heading).
        const at = centreOf(e);
        const gone = (f.x > 0 && at.x < -m - e.w / 2) || (f.x < 0 && at.x > w + m + e.w / 2)
          || (f.y < 0 && at.y > h + m + e.h / 2) || (f.y > 0 && at.y < -m - e.h / 2);
        if (gone) ctx.despawn(e, 'scrolled');
      }
      spawnDue(ctx, w, h);
      publish(ctx);
      const sector = ctx.scalar('sector.number', 1);
      if (sector !== read(s, 'sector')) { s.sector = sector; ctx.emit('sector', { sector }); }
      if (s.ended !== true && read(s, 'd') >= routeLength(c)) {
        s.ended = true;
        ctx.emit('route-end', {});
        ctx.signal('route-end', null, null, {});
      }
    },
  },
  view(ctx) {
    const { f } = axes(ctx.config.heading), d = read(ctx.state, 'd');
    return { x: f.x * d, y: f.y * d };
  },
});

function spawnDue(ctx: TrackCtx, w: number, h: number): void {
  const c = ctx.config, s = ctx.state, d = read(s, 'd'), route = s.route as JsonObject[], m = c.margin;
  // Farthest a view point lies from the anchor, plus slack for big kinds.
  const reach = Math.hypot(Math.max(c.anchor.x, w - c.anchor.x), Math.max(c.anchor.y, h - c.anchor.y)) + m + 4;
  let next = read(s, 'next');
  for (let i = next; i < route.length; i++) {
    const item = route[i]!, a = read(item, 'a');
    if (a - d > reach) break;
    if (item.spawned === true) { if (i === next) next++; continue; }
    if (a - d < -reach) { item.spawned = true; if (i === next) next++; continue; }
    const at = project(c, a, read(item, 'c'), d);
    if (at.x < -m || at.x > w + m || at.y < -m || at.y > h + m) continue;
    const e = ctx.spawn(item.archetype as string, { x: at.x, y: at.y,
      ...(typeof item.label === 'string' ? { label: item.label } : {}), ...(typeof item.tint === 'number' ? { tint: item.tint } : {}),
      params: { ...(typeof item.vx === 'number' ? { vx: item.vx, vy: item.vy as number } : {}) } });
    e.y += e.h / 2; // placements are centres
    item.spawned = true;
    if (i === next) next++;
  }
  s.next = next;
}

export type TurretConfig = {
  targetTag: string;
  range: number;
  /** Firing arc: the direction to the target (degrees, 0 = up, clockwise)
   * must lie within `arcHalf` of `arcCenter`. */
  arcCenter: number; arcHalf: number;
  /** Fires only while wholly this far inside the view. */
  inset: number;
  warnTicks: number; burstTicks: number; restTicks: number;
  /** Tag held while firing (a `weapon.gun` with this `armTag`). */
  armTag: string;
};

type TurretCtx = ModuleCtx<TurretConfig>;

function turretTarget(ctx: TurretCtx, e: EntityV1): EntityV1 | null {
  const c = ctx.config, { w, h } = ctx.world.bounds;
  if (e.x - e.w / 2 < c.inset || e.x + e.w / 2 > w - c.inset || e.y - e.h < c.inset || e.y > h - c.inset) return null;
  const from = centreOf(e);
  let best: { t: EntityV1; d: number } | null = null;
  for (const t of ctx.entities({ tag: c.targetTag, status: 'active' })) {
    const to = centreOf(t), dx = to.x - from.x, dy = to.y - from.y, dist = Math.hypot(dx, dy);
    if (dist > c.range || dist < 1e-6) continue;
    const bearing = (Math.atan2(dx, -dy) * 180) / Math.PI;
    let off = Math.abs(bearing - c.arcCenter) % 360;
    if (off > 180) off = 360 - off;
    if (off > c.arcHalf) continue;
    if (!best || dist < best.d) best = { t, d: dist };
  }
  return best?.t ?? null;
}

/** `ai.turret` — an emplacement (or any shooter) that only engages fairly:
 * while wholly on screen with a target in range and inside its firing arc it
 * telegraphs (swivel pose, blinking, a `warn` cue) for `warnTicks`, then
 * holds its arm tag for a burst (its gun fires), then rests. Losing the
 * target aborts the warning. */
export const turret = defineModule<TurretConfig>({
  id: 'ai.turret', version: 1, attach: 'entity', role: 'behaviour',
  summary: 'Fair emplacement: on screen, target in range and arc → telegraph (pose, blink, cue) → armed burst for its gun → rest; never fires off screen.',
  config: obj({ targetTag: id(), range: num(0.5, 64), arcCenter: num(-180, 180), arcHalf: num(1, 180), inset: num(0, 8),
    warnTicks: int(1, 300), burstTicks: int(1, 600), restTicks: int(0, 900), armTag: id() }),
  requires: ['weapon.gun'], events: ['warn'], presentation: { warn: { cue: 'countdown' } },
  proofs: ['silent-off-screen', 'silent-out-of-arc', 'telegraphs-before-firing', 'armed-for-burst', 'rests', 'aborts-without-target'],
  animStates: () => ['idle', 'action-1'],
  check: (c, context, archetype) => (archetype && !context.archetypes[archetype]?.modules.includes('weapon.gun') ? ['ai.turret needs a weapon.gun on its kind'] : []),
  init(ctx, e) { Object.assign(ctx.data(e), { phase: 'scan', t: 0 }); },
  systems: {
    think(ctx, e) {
      if (!e || e.status !== 'active') return;
      const c = ctx.config, d = ctx.data(e), target = turretTarget(ctx, e);
      if (target) e.facing = target.x < e.x ? -1 : 1;
      if (d.phase === 'scan') {
        if (target) { d.phase = 'warn'; d.t = c.warnTicks; ctx.emit('warn', { x: e.x, y: e.y - e.h / 2 }); }
        return;
      }
      d.t = read(d, 't') - 1;
      if (d.phase === 'warn') {
        if (!target) { d.phase = 'scan'; return; }
        if (read(d, 't') <= 0) { d.phase = 'fire'; d.t = c.burstTicks; addTag(e, c.armTag); }
      } else if (d.phase === 'fire') {
        if (read(d, 't') <= 0 || !target) { removeTag(e, c.armTag); d.phase = 'rest'; d.t = c.restTicks; }
      } else if (read(d, 't') <= 0) d.phase = 'scan';
    },
    present(ctx, e) {
      if (!e || e.status !== 'active') return;
      const d = ctx.data(e);
      if (d.phase === 'warn') {
        ctx.setAnim(e, 'action-1');
        if (read(d, 't') % 8 === 0) ctx.react(e, 'blink');
      } else if (d.phase !== 'fire') ctx.setAnim(e, 'idle');
    },
  },
});

export type TargetsConfig = {
  /** Tag of the primary targets (destroyed ones count). */
  targetTag: string;
  /** How many must be destroyed before extraction. */
  count: number;
  /** Signal that means the craft has reached extraction (`route-end`). */
  extractOn: string;
  /** Scalar in [0, 1] of the way to extraction (`route.progress`); counts as
   * the checklist's final item in `goal.progress`. */
  progressScalar: string;
  /** Points per seat on a successful extraction. */
  bonus: number;
};

/** `rule.targets` — the primary-target checklist: counts destroyed targets
 * (HUD `n/count`), and at extraction wins (sets `goal.done`, signals
 * `extracted` — a bomb bay cashes out on it) with every target down, or
 * fails the mission with fewer. Publishes `goal.progress`. */
export const targets = defineModule<TargetsConfig>({
  id: 'rule.targets', version: 1, attach: 'global', role: 'rule',
  summary: 'Primary-target checklist with extraction: counts destroyed targets; at extraction wins with all of them, otherwise the mission fails.',
  config: obj({ targetTag: id(), count: int(1, 20), extractOn: readsSignal(), progressScalar: id(), bonus: int(0, 100000) }),
  requires: ['flow.objective'], events: ['target-down', 'extract', 'abort'],
  presentation: { 'target-down': { cue: 'success', vfx: { effect: 'popup', text: 'TARGET' } }, extract: { cue: 'win' }, abort: { cue: 'lose' } },
  proofs: ['counts-destroyed-targets', 'wins-at-extraction-with-all', 'fails-at-extraction-short', 'goal-progress', 'extracted-signal'],
  animStates: () => [],
  hud: () => [{ id: 'targets', label: 'TARGETS', kind: 'text' }],
  hudValues: ctx => ({ targets: `${read(ctx.state, 'down')}/${ctx.config.count}` }),
  initGlobal(ctx) { Object.assign(ctx.state, { down: 0, blows: 0 }); ctx.setScalar('goal.progress', 0); },
  systems: {
    flow(ctx) {
      const c = ctx.config, s = ctx.state;
      // Blows landed on a target (a two-hit bridge's first bomb) are progress too.
      for (const sig of ctx.signals('damage')) {
        const tags = sig.data.tags;
        if (Array.isArray(tags) && tags.includes(c.targetTag)) s.blows = read(s, 'blows') + 1;
      }
      for (const sig of ctx.signals('defeated')) {
        const tags = sig.data.tags;
        if (!Array.isArray(tags) || !tags.includes(c.targetTag)) continue;
        s.down = read(s, 'down') + 1;
        ctx.emit('target-down', { x: sig.data.x as number, y: sig.data.y as number, down: read(s, 'down'), count: c.count });
      }
      const down = Math.min(c.count, read(s, 'down'));
      ctx.setScalar('goal.progress', (down + Math.min(1, ctx.scalar(c.progressScalar, 0))) / (c.count + 1));
      if (ctx.world.phase !== 'active' || !ctx.signals(c.extractOn).length) return;
      if (down >= c.count) {
        for (let slot = 0; slot < ctx.world.playerCount; slot++) ctx.addScore(slot, c.bonus);
        ctx.emit('extract', { down });
        ctx.signal('extracted', null, null, {});
        ctx.setScalar('goal.done', 1);
      } else {
        ctx.emit('abort', { down, count: c.count });
        ctx.end('lost', 'objective-incomplete');
      }
    },
  },
  // Each primary down (and each blow landed on one) is finished work worth
  // more than a bomb-lead pull.
  solverProgress: ctx => read(ctx.state, 'down') * 8 + read(ctx.state, 'blows') * 6,
});

export type ConditionStage = { atOrBelow: number; everyTicks: number; effect: 'smoke' | 'blaze'; speedMul: number };
export type ConditionConfig = { stages: ConditionStage[] };

/** `fx.condition` — visible damage stages for a `core.vitals` body: at or
 * below a stage's lives it puffs smoke or trails fire on a cadence and is
 * slowed (the speed protocol); entering the worst stage chirps a warning. */
export const condition = defineModule<ConditionConfig>({
  id: 'fx.condition', version: 1, attach: 'entity', role: 'presentation',
  summary: 'Damage stages over core.vitals: smoke or fire on a cadence below a lives threshold, slower steering, a warning on the worst stage.',
  config: obj({ stages: list(obj({ atOrBelow: int(0, 9), everyTicks: int(1, 120), effect: oneOf('smoke', 'blaze'),
    speedMul: num(0.1, 1) }), 4, 1) }),
  requires: ['core.vitals'], events: ['smoke', 'blaze', 'warning'],
  presentation: { smoke: { cue: 'none', vfx: { effect: 'burst', count: 3 } }, blaze: { cue: 'none', vfx: { effect: 'burst', palette: 'slot', count: 5 } },
    warning: { cue: 'countdown' } },
  proofs: ['stage-by-lives', 'cadence', 'slows', 'warns-once-on-worst'],
  animStates: () => [],
  check: (c, context, archetype) => (archetype && !context.archetypes[archetype]?.modules.includes('core.vitals') ? ['fx.condition needs core.vitals on its kind'] : []),
  init(ctx, e) { Object.assign(ctx.data(e), { speedMul: 1, warned: false }); },
  systems: {
    rules(ctx, e) {
      if (!e) return;
      const vitals = ctx.peer(e, 'core.vitals'), d = ctx.data(e);
      const lives = vitals ? read(vitals, 'lives') : 99;
      // The worst stage that applies (lowest threshold at or above lives).
      const stage = [...ctx.config.stages].sort((a, b) => a.atOrBelow - b.atOrBelow).find(s => lives <= s.atOrBelow) ?? null;
      d.speedMul = stage?.speedMul ?? 1;
      if (!stage || e.status !== 'active') return;
      const worst = Math.min(...ctx.config.stages.map(s => s.atOrBelow));
      if (stage.atOrBelow === worst && d.warned !== true) { d.warned = true; ctx.emit('warning', { slot: e.slot ?? 0 }); }
      if (ctx.world.tick % stage.everyTicks === 0) ctx.emit(stage.effect, { x: e.x, y: e.y - e.h / 2 - e.z, slot: e.slot ?? 0 });
    },
  },
});
